/**
 * Configured Axios Instance for TeleTime POS — Two-Tier Auth
 *
 * Token is read from memory (AuthContext.getToken), never localStorage.
 * Device session cookie (httpOnly) is sent automatically by the browser.
 * On 401: dispatches lock event (back to PIN screen), not full logout.
 */

import axios from 'axios';
import errorTracker from '../services/ErrorTracker';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  withCredentials: true, // Send httpOnly device session cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// TOKEN ACCESSOR
// Injected by AuthProvider on mount — reads token from memory, not storage.
// ============================================================================

let _getToken = () => null;

/**
 * Called by AuthProvider to inject the getToken function.
 * This avoids a circular dependency between axios and AuthContext.
 */
export function setTokenAccessor(fn) {
  _getToken = fn;
}

// ============================================================================
// REQUEST INTERCEPTOR
// Reads token from memory via the injected accessor
// ============================================================================

api.interceptors.request.use(
  (config) => {
    const token = _getToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (import.meta.env.DEV) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// ============================================================================
// TOKEN REFRESH LOGIC
// For two-tier auth, 401 means token expired → lock to PIN screen.
// Refresh uses the httpOnly device session cookie automatically.
// ============================================================================

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

function onTokenRefreshFailed(error) {
  refreshSubscribers.forEach((cb) => cb(null, error));
  refreshSubscribers = [];
}

// ============================================================================
// RESPONSE INTERCEPTOR
// On 401: lock to PIN screen (not full logout)
// ============================================================================

api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    const { response, request, message, config: originalRequest } = error;

    if (response) {
      const { status, data } = response;

      // Skip auth handling for login/init requests — 401 = bad credentials
      const isAuthRequest = originalRequest?.url?.includes('/auth/login')
        || originalRequest?.url?.includes('/auth/device-init')
        || originalRequest?.url?.includes('/auth/pin-login');

      // 401 Unauthorized — token expired, lock to PIN screen
      if (status === 401 && !originalRequest._retry && !isAuthRequest) {
        // If checkout is active, suppress immediate lock
        if (window.__posCheckoutActive) {
          console.warn('[API] 401 during active checkout — suppressing lock');
          return Promise.reject({
            status: 401,
            message: data?.message || 'Session expired',
            code: 'UNAUTHORIZED',
          });
        }

        // Lock to PIN screen
        console.warn('[API] 401 — locking to PIN screen');
        window.dispatchEvent(new CustomEvent('pos:auth-lock'));

        return Promise.reject({
          status: 401,
          message: data?.message || 'Session expired. Please enter your PIN.',
          code: 'UNAUTHORIZED',
        });
      }

      // 403 Forbidden
      if (status === 403) {
        return Promise.reject({
          status: 403,
          // A fraud refusal (middleware/fraudCheck.js) answers { error: '<message>', code } —
          // error is a string there, so read it rather than fall through to the generic text.
          message: data?.error?.message || data?.message
            || (typeof data?.error === 'string' ? data.error : 'You do not have permission to perform this action.'),
          code: data?.error?.code || data?.code || 'FORBIDDEN',
          // e.g. { approvalRequired, reasons } when a return needs a manager's PIN (R1b)
          details: data?.error?.details || null,
          // B5: the refusal's own evidence — risk score, signals, the rules that fired.
          // CheckoutModal opens FraudBlockedModal only when it has this, so dropping it
          // here is why a fraud-declined sale showed a plain error instead.
          fraudAssessment: data?.fraudAssessment || data?.error?.fraudAssessment || null,
          riskScore: data?.riskScore ?? null,
          alertId: data?.alertId ?? null,
        });
      }

      // 404 Not Found
      if (status === 404) {
        return Promise.reject({
          status: 404,
          message: data?.message || 'Resource not found.',
          code: 'NOT_FOUND',
        });
      }

      // 422/400 Validation Error
      if (status === 422 || status === 400) {
        return Promise.reject({
          status,
          message: data?.error?.message || data?.message || (typeof data?.error === 'string' ? data.error : 'Validation failed.'),
          code: data?.error?.code || 'VALIDATION_ERROR',
          details: data?.error?.details || data?.details || null,
        });
      }

      // 423 Locked (PIN lockout)
      if (status === 423) {
        return Promise.reject({
          status: 423,
          statusCode: 423,
          message: data?.message || 'Account temporarily locked',
          code: 'LOCKED',
          secondsRemaining: data?.secondsRemaining || data?.data?.secondsRemaining || 0,
        });
      }

      // 500+ Server Error
      if (status >= 500) {
        console.error('[Axios] Server error response:', JSON.stringify(data));
        errorTracker.captureNetworkError(error, {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status,
        });
        return Promise.reject({
          status,
          message: data?.error?.message || data?.message || 'Server error. Please try again later.',
          code: 'SERVER_ERROR',
          serverError: data,
        });
      }

      // Other errors
      return Promise.reject({
        status,
        message: data?.error?.message || data?.message || (typeof data?.error === 'string' ? data.error : 'An error occurred.'),
        code: data?.error?.code || data?.code || 'API_ERROR',
        details: data?.error?.details || data?.details || null,
      });
    }

    // No response — network error or timeout
    if (request) {
      if (error.code === 'ECONNABORTED' || message.includes('timeout')) {
        errorTracker.captureNetworkError(error, {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status: 0,
        });
        return Promise.reject({
          status: 0,
          message: 'Request timed out. Please check your connection and try again.',
          code: 'TIMEOUT',
        });
      }

      errorTracker.captureNetworkError(error, {
        url: originalRequest?.url,
        method: originalRequest?.method,
        status: 0,
      });
      return Promise.reject({
        status: 0,
        message: 'Network error. Please check your internet connection.',
        code: 'NETWORK_ERROR',
      });
    }

    return Promise.reject({
      status: 0,
      message: message || 'An unexpected error occurred.',
      code: 'UNKNOWN_ERROR',
    });
  }
);

// ============================================================================
// AUTH HELPER FUNCTIONS (Memory-only — no localStorage)
// ============================================================================

/**
 * @deprecated Use AuthContext.getToken() instead.
 * Kept for backward compat during migration.
 */
export const setAuthToken = (token) => {
  // No-op in two-tier auth — token is managed by AuthContext
};

export const getAuthToken = () => {
  return _getToken();
};

export const setUserData = () => {
  // No-op in two-tier auth
};

export const getUserData = () => {
  return null;
};

export const clearAuth = () => {
  // Dispatch lock event — AuthContext handles state cleanup
  window.dispatchEvent(new CustomEvent('pos:auth-lock'));
};

export const isAuthenticated = () => {
  return !!_getToken();
};

// ============================================================================
// REQUEST UTILITIES
// ============================================================================

export const createCancellableRequest = (method, url, data = null, config = {}) => {
  const controller = new AbortController();
  const requestConfig = { ...config, signal: controller.signal };

  let promise;
  switch (method.toLowerCase()) {
    case 'get':
      promise = api.get(url, requestConfig);
      break;
    case 'post':
      promise = api.post(url, data, requestConfig);
      break;
    case 'put':
      promise = api.put(url, data, requestConfig);
      break;
    case 'patch':
      promise = api.patch(url, data, requestConfig);
      break;
    case 'delete':
      promise = api.delete(url, requestConfig);
      break;
    default:
      throw new Error(`Unsupported HTTP method: ${method}`);
  }

  return { promise, cancel: () => controller.abort() };
};

export const requestWithRetry = async (method, url, data = null, options = {}) => {
  const { retries = 3, retryDelay = 1000, ...config } = options;
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      switch (method.toLowerCase()) {
        case 'get':
          return await api.get(url, config);
        case 'post':
          return await api.post(url, data, config);
        case 'put':
          return await api.put(url, data, config);
        case 'patch':
          return await api.patch(url, data, config);
        case 'delete':
          return await api.delete(url, config);
        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }
    } catch (error) {
      lastError = error;
      const status = error.status || error.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw error;
      }
      if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
        throw error;
      }
      if (attempt < retries) {
        const delay = retryDelay * Math.pow(2, attempt);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

export default api;
